import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Copy, Download, Save, MoveDiagonal, Lock } from "lucide-react";
import { toPng } from "html-to-image";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import ChartGrid from "../components/charts/ChartGrid";
import ChartFormDialog from "../components/charts/ChartFormDialog";
import AlarmFormDialog from "../components/charts/AlarmFormDialog";
import AlarmEventsSheet from "../components/charts/AlarmEventsSheet";
import TimeRangeBar, { rangeFromPreset } from "../components/charts/TimeRangeBar";
import { toast } from "sonner";

export default function DashboardDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const t = useT();
  const [dashboard, setDashboard] = useState(null);
  const [charts, setCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChartForm, setShowChartForm] = useState(false);
  const [editingChart, setEditingChart] = useState(null);
  const [alarmFor, setAlarmFor] = useState(null);
  const [showAlarm, setShowAlarm] = useState(false);
  const [eventsAlarm, setEventsAlarm] = useState(null);
  const [showEvents, setShowEvents] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [layout, setLayout] = useState([]);

  // Time range and refresh controls (synced across all charts on the page)
  const [range, setRange] = useState(() => rangeFromPreset("24h"));
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const gridRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        api.get(`/dashboards/${id}`),
        api.get(`/dashboards/${id}/charts`),
      ]);
      setDashboard(d.data);
      setCharts(c.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function deleteChart(chart) {
    if (!window.confirm(`Delete chart "${chart.title}"?`)) return;
    try {
      await api.delete(`/charts/${chart.id}`);
      setCharts((prev) => prev.filter((c) => c.id !== chart.id));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  async function persistChart(chartId, patch) {
    try {
      await api.patch(`/charts/${chartId}`, patch);
      setCharts((prev) =>
        prev.map((c) => (c.id === chartId ? { ...c, ...patch } : c))
      );
    } catch (e) {
      toast.error("Failed to save chart size");
    }
  }

  async function duplicate() {
    try {
      const { data } = await api.post(`/dashboards/${id}/duplicate`);
      toast.success("Dashboard duplicated");
      navigate(`/dashboards/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  async function exportData(fmt) {
    try {
      const params = { fmt };
      if (range?.from) params.from = range.from;
      if (range?.to) params.to = range.to;
      const res = await api.get(`/dashboards/${id}/export`, {
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${dashboard?.name || "dashboard"}.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  }

  async function exportPng() {
    if (!gridRef.current) return;
    try {
      toast.message("Generando PNG…");
      const dataUrl = await toPng(gridRef.current, {
        cacheBust: true,
        backgroundColor: getComputedStyle(document.body).backgroundColor || "#ffffff",
        pixelRatio: 2,
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${dashboard?.name || "dashboard"}.png`;
      a.click();
    } catch (e) {
      toast.error("No se pudo generar el PNG");
      console.warn(e);
    }
  }

  function refreshAll() {
    setRefreshing(true);
    setRefreshKey((k) => k + 1);
    // Brief visual feedback — children handle their own loading state.
    setTimeout(() => setRefreshing(false), 600);
  }

  function applyChartUpdate(chartId, patch) {
    setCharts((prev) =>
      prev.map((c) => (c.id === chartId ? { ...c, ...patch } : c))
    );
  }

  async function saveLayout() {
    try {
      await api.patch(`/dashboards/${id}`, {
        layout: layout.length
          ? layout.map((it) => ({ id: it.i, x: it.x, y: it.y, w: it.w, h: it.h }))
          : charts.map((c) => ({ id: c.id, w: c.width, h: c.height })),
      });
      toast.success("Layout saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  function showAlarmEvents(alarm, chart) {
    setEventsAlarm({ ...alarm, _chartTitle: chart?.title });
    setShowEvents(true);
  }

  if (loading || !dashboard) {
    return <div className="text-sm text-muted-foreground">{t("detail.loading")}</div>;
  }

  return (
    <div className="space-y-6" data-testid="dashboard-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <button
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center mb-1"
            onClick={() => navigate("/dashboards")}
            data-testid="back-to-dashboards"
          >
            <ArrowLeft className="h-3 w-3 mr-1" /> {t("detail.back")}
          </button>
          <h1 className="text-2xl font-bold tracking-tight truncate" data-testid="dashboard-title">
            {dashboard.name}
          </h1>
          <p className="text-sm text-muted-foreground">
            {dashboard.description || "—"} {t(charts.length === 1 ? "detail.charts_count_one" : "detail.charts_count_many", { n: charts.length })}
            {editMode ? <> · <span className="text-primary font-medium">{t("detail.edit_mode_hint")}</span></> : null}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <Button
                variant={editMode ? "default" : "outline"}
                onClick={() => setEditMode((v) => !v)}
                data-testid="toggle-edit-mode-btn"
              >
                {editMode ? <Lock className="h-4 w-4 mr-1.5" /> : <MoveDiagonal className="h-4 w-4 mr-1.5" />}
                {editMode ? t("detail.lock_layout") : t("detail.edit_layout")}
              </Button>
              <Button variant="outline" onClick={saveLayout} data-testid="save-layout-btn">
                <Save className="h-4 w-4 mr-1.5" /> {t("detail.save_layout")}
              </Button>
              <Button variant="outline" onClick={duplicate} data-testid="duplicate-dashboard-btn">
                <Copy className="h-4 w-4 mr-1.5" /> {t("detail.duplicate")}
              </Button>
            </>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-dashboard-btn">
                <Download className="h-4 w-4 mr-1.5" /> {t("detail.export")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportData("csv")} data-testid="export-csv">CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportData("xlsx")} data-testid="export-xlsx">XLSX</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportData("pdf")} data-testid="export-pdf">PDF</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {isAdmin && (
            <Button
              onClick={() => {
                setEditingChart(null);
                setShowChartForm(true);
              }}
              data-testid="add-chart-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> {t("detail.add_chart")}
            </Button>
          )}
        </div>
      </div>

      {charts.length === 0 ? (
        <Card className="card-soft">
          <CardContent className="py-16 flex flex-col items-center text-center">
            <div className="h-12 w-12 rounded-full bg-primary/10 grid place-items-center mb-4">
              <Plus className="h-5 w-5 text-primary" />
            </div>
            <h3 className="font-semibold">{t("detail.empty_title")}</h3>
            <p className="text-sm text-muted-foreground max-w-sm mt-1">
              {isAdmin ? t("detail.empty_charts_admin") : t("detail.empty_charts_user")}
            </p>
            {isAdmin && (
              <Button
                className="mt-5"
                onClick={() => {
                  setEditingChart(null);
                  setShowChartForm(true);
                }}
                data-testid="empty-add-chart"
              >
                <Plus className="h-4 w-4 mr-1.5" /> {t("detail.add_chart")}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <TimeRangeBar
            range={range}
            onChange={setRange}
            onRefreshAll={refreshAll}
            onExportCsv={() => exportData("csv")}
            onExportPng={exportPng}
            refreshing={refreshing}
          />
          <div ref={gridRef}>
            <ChartGrid
              charts={charts}
              canEdit={isAdmin && editMode}
              isAdmin={isAdmin}
              onEdit={(chart) => {
                setEditingChart(chart);
                setShowChartForm(true);
              }}
              onDelete={deleteChart}
              onAddAlarm={(chart) => {
                setAlarmFor(chart);
                setShowAlarm(true);
              }}
              onShowEvents={isAdmin ? showAlarmEvents : undefined}
              onLayoutChange={setLayout}
              onPersistChart={persistChart}
              onChartUpdate={applyChartUpdate}
              range={range}
              refreshKey={refreshKey}
            />
          </div>
        </>
      )}

      <ChartFormDialog
        open={showChartForm}
        onOpenChange={setShowChartForm}
        dashboardId={id}
        chart={editingChart}
        onSaved={load}
      />
      <AlarmFormDialog
        open={showAlarm}
        onOpenChange={setShowAlarm}
        chart={alarmFor}
        onSaved={load}
      />
      <AlarmEventsSheet
        open={showEvents}
        onOpenChange={setShowEvents}
        alarm={eventsAlarm}
      />
    </div>
  );
}
